import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import { console_, type DocumentKindOption, type VerbunkDocument } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { when } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S21b. ONE STORED DOCUMENT, AND WHO HAS OPENED IT.
//
// The vault's three reads existed since V7 and no screen called them: evidence went in
// with a credential and could never be looked at again, and the access log §V7.3 keeps
// so that "who looked at this passport" has an answer was readable by nobody.
//
// THE METADATA LOADS ON ARRIVAL; THE BYTES ONLY ON THE BUTTON. Every content read writes
// an access row, so rendering this page must not count as opening the document.
//
// A DOWNLOAD, NOT A PREVIEW. A blob URL runs with this page's origin, and the content
// type is the uploader's claim — opening it in a tab would trust that claim.
// ---------------------------------------------------------------------------

type Access = { id: string; purpose_label: string; occurred_at: string; by_person: boolean };

export function DocumentView() {
  const { id = "" } = useParams();
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const [doc, setDoc] = useState<VerbunkDocument | null>(null);
  const [kinds, setKinds] = useState<DocumentKindOption[]>([]);
  const [log, setLog] = useState<Access[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mayReadLog = me ? may(me, "compliance.read") : false;

  const loadLog = useCallback(async () => {
    if (!mayReadLog) return;
    const res = await send<{ access?: Access[] }>(console_.documentAccessLog(apiBase, id));
    if (res.status === 200 && res.body.access) setLog(res.body.access);
  }, [apiBase, id, mayReadLog]);

  useEffect(() => {
    void (async () => {
      try {
        const res = await send<{ document?: VerbunkDocument; error?: { message?: string } }>(
          console_.getDocument(apiBase, id),
        );
        if (res.status !== 200 || !res.body.document) {
          setFailed(refusal(res.body.error) ?? t("This document could not be read."));
          return;
        }
        setDoc(res.body.document);
        const k = await send<{ document_kinds?: DocumentKindOption[] }>({
          method: "GET",
          url: `${apiBase}/v1/reference/document-kinds`,
          headers: {},
          credentials: "include",
        });
        if (k.status === 200 && k.body.document_kinds) setKinds(k.body.document_kinds);
        await loadLog();
      } catch {
        setFailed(t("We could not reach Verbunk."));
      }
    })();
  }, [apiBase, id, loadLog]);

  async function download() {
    if (!doc) return;
    setBusy(true);
    setFailed(null);
    try {
      const spec = console_.documentContent(apiBase, id);
      const res = await fetch(spec.url, { method: spec.method, headers: spec.headers, credentials: spec.credentials });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
        setFailed(refusal(body.error) ?? t("This document could not be opened."));
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
      await loadLog();
    } catch {
      setFailed(t("We could not reach Verbunk."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{doc?.filename ?? t("Document")}</h2>
      </div>

      {failed ? (
        <div className="form-message" role="alert">
          <StatusChip tone="failed" label={t("Not read")} />
          <span>{failed}</span>
        </div>
      ) : null}
      {!doc && !failed ? <p className="muted">{t("Reading the document…")}</p> : null}

      {doc ? (
        <>
          <dl className="facts">
            <dt>{t("Kind")}</dt>
            <dd>{kinds.find((k) => k.id === doc.kind)?.label ?? "—"}</dd>
            <dt>{t("Size")}</dt>
            <dd>{Math.ceil(doc.byte_size / 1024)} KB</dd>
            <dt>{t("Stored")}</dt>
            <dd>{when(doc.uploaded_at)}</dd>
            <dt>{t("Deleted after")}</dt>
            <dd>{doc.delete_after ? when(doc.delete_after) : t("No date set")}</dd>
          </dl>
          <p>
            <button type="button" className="btn" disabled={busy} onClick={() => void download()}>
              {busy ? t("Working…") : t("Download")}
            </button>
          </p>
          <p className="muted">{t("Downloading is recorded in the access log below, with the time.")}</p>
          {doc.worker_id ? (
            <p>
              <Link to={`/console/workers/${doc.worker_id}`}>{t("Back to their bench record")}</Link>
            </p>
          ) : null}

          <h3>{t("Who has opened it")}</h3>
          {!mayReadLog ? (
            <p className="muted">{t("The access log is read by a compliance user, an admin or the owner.")}</p>
          ) : log === null ? (
            <p className="muted">{t("Reading the access log…")}</p>
          ) : log.length === 0 ? (
            <p className="muted">{t("Nobody has opened this document.")}</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">{t("When")}</th>
                  <th scope="col">{t("Why")}</th>
                  <th scope="col">{t("By")}</th>
                </tr>
              </thead>
              <tbody>
                {log.map((a) => (
                  <tr key={a.id}>
                    <td>{when(a.occurred_at)}</td>
                    <td>{translateLabel(locale, a.purpose_label)}</td>
                    <td>{a.by_person ? t("A person") : t("The system")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </section>
  );
}
